/**
 * Password validation utilities
 *
 * Requirements (as per backend validation):
 * - Minimum 8 characters
 * - At least 1 uppercase letter (A-Z)
 * - At least 1 lowercase letter (a-z)
 * - At least 1 number (0-9)
 * - At least 1 special character (@$!%*?&#)
 */

export const PASSWORD_REQUIREMENTS = {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    requireSpecial: true,
    specialChars: '@$!%*?&#'
};

/**
 * Validate password against all requirements
 * @param {string} password - Password to validate
 * @returns {object} - { isValid: boolean, errors: string[] }
 */
export const validatePassword = (password) => {
    const errors = [];

    if (!password || password.length < PASSWORD_REQUIREMENTS.minLength) {
        errors.push(`Minimum ${PASSWORD_REQUIREMENTS.minLength} characters required`);
    }

    if (!/[A-Z]/.test(password)) {
        errors.push('At least 1 uppercase letter (A-Z) required');
    }

    if (!/[a-z]/.test(password)) {
        errors.push('At least 1 lowercase letter (a-z) required');
    }

    if (!/[0-9]/.test(password)) {
        errors.push('At least 1 number (0-9) required');
    }

    const specialCharRegex = new RegExp(`[${PASSWORD_REQUIREMENTS.specialChars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`);
    if (!specialCharRegex.test(password)) {
        errors.push(`At least 1 special character (${PASSWORD_REQUIREMENTS.specialChars}) required`);
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Get password strength level
 * @param {string} password - Password to check
 * @returns {object} - { strength: string, score: number }
 */
export const getPasswordStrength = (password) => {
    if (!password) {
        return { strength: 'none', score: 0 };
    }

    let score = 0;

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;

    // Character variety
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (new RegExp(`[${PASSWORD_REQUIREMENTS.specialChars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password)) score += 1;

    // Determine strength
    if (score <= 2) return { strength: 'weak', score };
    if (score <= 4) return { strength: 'medium', score };
    if (score <= 6) return { strength: 'good', score };
    return { strength: 'strong', score };
};

/**
 * Format password requirements as a readable list
 * @returns {string[]} - Array of requirement strings
 */
export const getPasswordRequirementsList = () => {
    return [
        `Minimum ${PASSWORD_REQUIREMENTS.minLength} characters`,
        'At least 1 uppercase letter (A-Z)',
        'At least 1 lowercase letter (a-z)',
        'At least 1 number (0-9)',
        `At least 1 special character (${PASSWORD_REQUIREMENTS.specialChars})`
    ];
};
